import { forwardRef } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useIntl } from "react-intl";
import { Slot } from "@radix-ui/react-slot";
import classNames from "classnames/bind";

import styles from "./Breadcrumb.module.scss";
const cx = classNames.bind(styles);

type BreadcrumbProps = ComponentPropsWithoutRef<"nav"> & {
	/** Accessible name for the navigation landmark, already translated. Defaults to the "breadcrumb.label" translation. */
	label?: string;
};

/** Navigation landmark wrapping the breadcrumb trail. */
export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
	({ label, className, children, ...props }, ref) => {
		const { formatMessage } = useIntl();
		const accessibleLabel = label ?? formatMessage({ id: "breadcrumb.label" });

		return (
			<nav
				ref={ref}
				aria-label={accessibleLabel}
				className={className}
				data-testid="breadcrumb"
				{...props}
			>
				{children}
			</nav>
		);
	}
);

Breadcrumb.displayName = "Breadcrumb";

type BreadcrumbListProps = ComponentPropsWithoutRef<"ol">;

/** Ordered list holding the breadcrumb items. */
export const BreadcrumbList = forwardRef<HTMLOListElement, BreadcrumbListProps>(
	({ className, ...props }, ref) => (
		<ol ref={ref} className={cx("breadcrumb__list", className)} {...props} />
	)
);

BreadcrumbList.displayName = "BreadcrumbList";

type BreadcrumbItemProps = ComponentPropsWithoutRef<"li">;

/** Single item in the breadcrumb trail. */
export const BreadcrumbItem = forwardRef<HTMLLIElement, BreadcrumbItemProps>(
	({ className, ...props }, ref) => (
		<li ref={ref} className={cx("breadcrumb__item", className)} {...props} />
	)
);

BreadcrumbItem.displayName = "BreadcrumbItem";

type BreadcrumbLinkProps = ComponentPropsWithoutRef<"a"> & {
	/** Merge props onto the child element instead of rendering an anchor. */
	asChild?: boolean;
};

/** Interactive link for a non-current breadcrumb item. */
export const BreadcrumbLink = forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
	({ asChild, className, ...props }, ref) => {
		const Component = asChild ? Slot : "a";

		return <Component ref={ref} className={cx("breadcrumb__link", className)} {...props} />;
	}
);

BreadcrumbLink.displayName = "BreadcrumbLink";

type BreadcrumbPageProps = ComponentPropsWithoutRef<"span">;

/** Current page, rendered as non-interactive text. */
export const BreadcrumbPage = forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
	({ className, ...props }, ref) => (
		<span
			ref={ref}
			role="link"
			aria-disabled="true"
			aria-current="page"
			className={cx("breadcrumb__page", className)}
			{...props}
		/>
	)
);

BreadcrumbPage.displayName = "BreadcrumbPage";

type BreadcrumbSeparatorProps = ComponentPropsWithoutRef<"li"> & {
	children?: ReactNode;
};

/** Visual separator between items, hidden from assistive technology. */
export const BreadcrumbSeparator = ({
	className,
	children,
	...props
}: BreadcrumbSeparatorProps) => (
	<li
		role="presentation"
		aria-hidden="true"
		className={cx("breadcrumb__separator", className)}
		{...props}
	>
		{children ?? "/"}
	</li>
);

BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

type BreadcrumbEllipsisProps = ComponentPropsWithoutRef<"span"> & {
	/** Accessible label for the collapsed items. Must be already translated. */
	label: string;
};

/** Indicates collapsed items in the trail. */
export const BreadcrumbEllipsis = ({ label, className, ...props }: BreadcrumbEllipsisProps) => (
	<span role="presentation" className={cx("breadcrumb__ellipsis", className)} {...props}>
		<span aria-hidden="true">…</span>
		<span className={cx("breadcrumb__ellipsis-label")}>{label}</span>
	</span>
);

BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";
